import type { ElementType } from "react";
import type { Theme } from "@mui/material/styles";
import dayjs, { type Dayjs } from "dayjs";

import { WEARABLE_LOINC_LIST } from "@/services/fhir/observation/wearableObservationBuilder";
import { formatDistanceFromMeters } from "@/utils/wearableFitFormat";

import {
  getPrimaryLoincCode,
  getWearableMedicalCardPresentation,
} from "./wearableMedicalCardPresentation";

export type WearableViewMode = "fitness" | "sleep";

/** LOINCs enviados pelo app FluxHealth (sono, SpO2, temperatura de pele). */
export const FLUXHEALTH_SLEEP_LOINC_CODES = [
  "93832-4",
  "93828-2",
  "93829-0",
  "93830-8",
  "93831-6",
  "59408-5",
  "96634-8",
  "81264-4",
] as const;

const FLUXHEALTH_SLEEP_LOINC_SET = new Set<string>(FLUXHEALTH_SLEEP_LOINC_CODES);

const FLUXHEALTH_SLEEP_LABELS: Record<string, string> = {
  "93832-4": "Sono (total)",
  "93828-2": "Sono: acordado",
  "93829-0": "Sono: REM",
  "93830-8": "Sono: leve",
  "93831-6": "Sono: profundo",
  "59408-5": "SpO2",
  "96634-8": "SpO2 abaixo de 90%",
  "81264-4": "Temperatura de pele (sono)",
};

export const WEARABLE_LOINC_CODES = WEARABLE_LOINC_LIST.map((d) => d.loinc) as readonly string[];

const WEARABLE_LOINC_CODE_SET = new Set(WEARABLE_LOINC_CODES);

const SLEEP_DURATION_LOINCS = new Set([
  "93832-4",
  "93828-2",
  "93829-0",
  "93830-8",
  "93831-6",
  "96634-8",
  "68516-4",
]);

export type WearableAggregationMode = "sum" | "average";

export interface FhirObservationResource {
  id?: string;
  resourceType?: string;
  code?: {
    text?: string;
    coding?: Array<{ code?: string; display?: string; system?: string }>;
  };
  effectiveDateTime?: string;
  effectivePeriod?: { start?: string; end?: string };
  issued?: string;
  performer?: Array<{ reference?: string }>;
  subject?: { reference?: string };
  valueQuantity?: { value?: number; unit?: string; code?: string };
  component?: Array<{
    valueQuantity?: { value?: number; unit?: string; code?: string };
    code?: { coding?: Array<{ code?: string }>; text?: string };
  }>;
  meta?: { lastUpdated?: string };
}

export interface FhirObservationEntry {
  resource?: FhirObservationResource;
}

export interface WearableMetricDefinition {
  loinc: string;
  label: string;
  unit: string;
  aggregationMode: WearableAggregationMode;
}

export interface DailyMetricPoint {
  dateKey: string;
  dateLabel: string;
  value: number | null;
  displayValue: string;
}

export interface WearableMetricSummary {
  loinc: string;
  label: string;
  unit: string;
  color: string;
  icon: ElementType;
  aggregatedValue: string;
  aggregatedNumeric: number | null;
  hasData: boolean;
  dailyPoints: DailyMetricPoint[];
}

export interface WearablePeriod {
  periodStart: Dayjs;
  periodEnd: Dayjs;
}

const SUM_LOINCS = new Set(["55423-8", "9052-2", "13184-2", "68516-4", "93832-4"]);

const AVG_LOINCS = new Set(["8867-4", "2339-0", "29463-7", "85354-9"]);

function getAggregationMode(loinc: string): WearableAggregationMode {
  if (SUM_LOINCS.has(loinc)) return "sum";
  if (AVG_LOINCS.has(loinc)) return "average";
  return "average";
}

function getMetricUnit(loinc: string): string {
  switch (loinc) {
    case "55423-8":
      return "passos";
    case "9052-2":
      return "kcal";
    case "13184-2":
      return "";
    case "68516-4":
    case "93832-4":
      return "min";
    case "8867-4":
      return "bpm";
    case "2339-0":
      return "mg/dL";
    case "29463-7":
      return "kg";
    case "85354-9":
      return "mmHg";
    default:
      return "";
  }
}

/** Resolve início/fim: selectedDate = fim do intervalo (âncora); início = N dias para trás. */
export function resolveWearablePeriod(params: {
  selectedDate: Dayjs;
  rangeDays: number;
  today?: Dayjs;
}): WearablePeriod {
  const today = (params.today ?? dayjs()).startOf("day");
  const anchorEnd = params.selectedDate.startOf("day");
  const periodEnd = anchorEnd.isAfter(today, "day") ? today : anchorEnd;
  const periodStart = periodEnd.subtract(params.rangeDays - 1, "day");
  return { periodStart, periodEnd };
}

/** Formata rótulo do período na barra de filtros. */
export function formatWearablePeriodLabel(
  periodStart: Dayjs,
  periodEnd: Dayjs,
  rangeDays: number
): string {
  if (rangeDays === 1) {
    return periodEnd.format("DD/MM/YYYY");
  }
  return `${periodStart.format("DD/MM/YYYY")}–${periodEnd.format("DD/MM/YYYY")}`;
}

/** Catálogo fixo de 9 métricas wearable (ordem do modal fitness). */
export function buildWearableMetricCatalog(): WearableMetricDefinition[] {
  return WEARABLE_LOINC_LIST.map((def) => ({
    loinc: def.loinc,
    label: def.text,
    unit: getMetricUnit(def.loinc),
    aggregationMode: getAggregationMode(def.loinc),
  }));
}

function getFluxHealthSleepMetricUnit(loinc: string): string {
  switch (loinc) {
    case "59408-5":
      return "%";
    case "81264-4":
      return "°C";
    case "93832-4":
    case "93828-2":
    case "93829-0":
    case "93830-8":
    case "93831-6":
    case "96634-8":
      return "min";
    default:
      return "";
  }
}

function getFluxHealthSleepAggregationMode(loinc: string): WearableAggregationMode {
  if (loinc === "59408-5" || loinc === "81264-4") return "average";
  return "sum";
}

/** Catálogo fixo de 8 métricas FluxHealth (modo sono). */
export function buildFluxHealthSleepMetricCatalog(): WearableMetricDefinition[] {
  return FLUXHEALTH_SLEEP_LOINC_CODES.map((loinc) => ({
    loinc,
    label: FLUXHEALTH_SLEEP_LABELS[loinc] ?? loinc,
    unit: getFluxHealthSleepMetricUnit(loinc),
    aggregationMode: getFluxHealthSleepAggregationMode(loinc),
  }));
}

export function getWearableMetricCatalogForMode(
  mode: WearableViewMode
): WearableMetricDefinition[] {
  return mode === "sleep" ? buildFluxHealthSleepMetricCatalog() : buildWearableMetricCatalog();
}

export function getWearableKpiCountForMode(mode: WearableViewMode): number {
  return getWearableMetricCatalogForMode(mode).length;
}

/**
 * FluxHealth grava Observations com subject do paciente; performer pode ser app/dispositivo.
 * No modo sono filtramos por LOINC + subject (quando presente), sem exigir performer Patient.
 */
function matchesPatientSubject(
  resource: FhirObservationResource,
  patientSubjectReference: string
): boolean {
  if (!patientSubjectReference) return true;
  const subjectRef = resource.subject?.reference;
  if (!subjectRef) return true;
  return subjectRef === patientSubjectReference;
}

export function filterWearableObservationEntries(
  entries: FhirObservationEntry[],
  options: {
    mode: WearableViewMode;
    patientPerformerReference: string;
    patientSubjectReference?: string;
  }
): FhirObservationEntry[] {
  const { mode, patientPerformerReference, patientSubjectReference = "" } = options;
  const allowedLoincs =
    mode === "sleep" ? FLUXHEALTH_SLEEP_LOINC_SET : WEARABLE_LOINC_CODE_SET;

  const result = entries.filter((entry) => {
    const resource = entry?.resource;
    if (!resource || resource.resourceType !== "Observation") return false;
    const code = getPrimaryLoincCode(resource);
    if (!code || !allowedLoincs.has(code)) return false;

    if (mode === "sleep") {
      return matchesPatientSubject(resource, patientSubjectReference);
    }

    const performerRef = resource.performer?.[0]?.reference;
    const isFromPatient =
      !patientPerformerReference || performerRef === patientPerformerReference;
    return isFromPatient;
  });

  return result;
}

const SLEEP_WAKE_DAY_LOINCS = new Set(["93832-4"]);

/**
 * Data efetiva para filtro/agregação diária.
 * Sono total (93832-4) com effectivePeriod: usa o fim do período (dia em que o paciente acordou).
 */
export function getObservationEffectiveDate(resource: FhirObservationResource): Dayjs | null {
  if (resource.effectiveDateTime) {
    const parsed = dayjs(resource.effectiveDateTime);
    return parsed.isValid() ? parsed : null;
  }

  const loinc = getPrimaryLoincCode(resource);
  const periodEnd = resource.effectivePeriod?.end;
  const periodStart = resource.effectivePeriod?.start;

  if (loinc && SLEEP_WAKE_DAY_LOINCS.has(loinc) && periodEnd) {
    const parsed = dayjs(periodEnd);
    return parsed.isValid() ? parsed : null;
  }

  const effective = periodStart ?? periodEnd;
  if (!effective) return null;
  const parsed = dayjs(effective);
  return parsed.isValid() ? parsed : null;
}

export function filterEntriesByDateRange(
  entries: FhirObservationEntry[],
  startDate: Dayjs,
  endDate: Dayjs
): FhirObservationEntry[] {
  const startKey = startDate.format("YYYY-MM-DD");
  const endKey = endDate.format("YYYY-MM-DD");

  const result = entries.filter((entry) => {
    const resource = entry?.resource;
    if (!resource) return false;
    const effectiveDate = getObservationEffectiveDate(resource);
    if (!effectiveDate) return false;
    const dateKey = effectiveDate.format("YYYY-MM-DD");
    return dateKey >= startKey && dateKey <= endKey;
  });

  return result;
}

/** Extrai valor numérico para agregação e gráfico. */
export function extractNumericValueFromObservation(
  resource: FhirObservationResource
): number | null {
  const loinc = getPrimaryLoincCode(resource);
  if (!loinc) return null;

  if (loinc === "85354-9") {
    const systolic = resource.component?.[0]?.valueQuantity?.value;
    return typeof systolic === "number" && Number.isFinite(systolic) ? systolic : null;
  }

  if (loinc === "81264-4") {
    const avgValue = resource.valueQuantity?.value;
    return typeof avgValue === "number" && Number.isFinite(avgValue) ? avgValue : null;
  }

  const value = resource.valueQuantity?.value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function formatSleepDurationFromMinutes(totalMinutes: number): string {
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const minutes = rounded % 60;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, "0")}min`;
  return `${minutes}min`;
}

function formatNumericForDisplay(loinc: string, value: number, includeUnit = false): string {
  switch (loinc) {
    case "55423-8":
    case "9052-2":
      return value.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
    case "93832-4":
    case "68516-4":
    case "93828-2":
    case "93829-0":
    case "93830-8":
    case "93831-6":
    case "96634-8":
      return formatSleepDurationFromMinutes(value);
    case "59408-5":
      return includeUnit ? `${value.toFixed(1)} %` : value.toFixed(1);
    case "81264-4":
      return includeUnit ? `${value.toFixed(1)} °C` : value.toFixed(1);
    case "29463-7":
      return includeUnit ? `${value.toFixed(1)} kg` : value.toFixed(1);
    case "8867-4":
      return includeUnit ? `${Math.round(value)} bpm` : `${Math.round(value)}`;
    case "2339-0":
      return includeUnit ? `${value.toFixed(0)} mg/dL` : value.toFixed(0);
    case "85354-9":
      return includeUnit ? `${Math.round(value)} mmHg` : `${Math.round(value)}`;
    case "13184-2":
      return formatDistanceFromMeters(value);
    default:
      if (SLEEP_DURATION_LOINCS.has(loinc)) {
        return formatSleepDurationFromMinutes(value);
      }
      if (Number.isInteger(value)) return value.toLocaleString("pt-BR");
      return value.toFixed(1);
  }
}

function aggregateValues(
  values: number[],
  mode: WearableAggregationMode,
  rangeDays: number
): number | null {
  if (values.length === 0) return null;
  // Um único envio (ou dedupe diário): usa o valor direto, sem somar reenvios.
  if (values.length === 1) return values[0];
  if (mode === "sum") {
    return values.reduce((acc, v) => acc + v, 0);
  }
  if (rangeDays === 1) {
    return values[values.length - 1];
  }
  return values.reduce((acc, v) => acc + v, 0) / values.length;
}

function buildDateKeysInRange(startDate: Dayjs, endDate: Dayjs): string[] {
  const keys: string[] = [];
  let cursor = startDate.startOf("day");
  const end = endDate.startOf("day");
  while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
    keys.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }
  return keys;
}

/** Timestamp para escolher a observation mais recente no dedupe diário. */
function getObservationRecencyTimestamp(resource: FhirObservationResource): number {
  if (resource.issued) {
    const issued = dayjs(resource.issued);
    if (issued.isValid()) return issued.valueOf();
  }
  if (resource.meta?.lastUpdated) {
    const lastUpdated = dayjs(resource.meta.lastUpdated);
    if (lastUpdated.isValid()) return lastUpdated.valueOf();
  }
  const effective = getObservationEffectiveDate(resource);
  if (effective) return effective.valueOf();
  const id = Number(resource.id);
  return Number.isFinite(id) ? id : 0;
}

/** Mantém apenas a observation mais recente por dia (múltiplos envios no mesmo dia). */
function dedupeMetricEntriesByDay(
  entries: FhirObservationEntry[],
  loinc: string
): FhirObservationEntry[] {
  const matching = entries.filter((entry) => getPrimaryLoincCode(entry?.resource) === loinc);
  if (matching.length <= 1) return entries;

  const others = entries.filter((entry) => getPrimaryLoincCode(entry?.resource) !== loinc);
  const byDay = new Map<string, FhirObservationEntry>();

  matching.forEach((entry) => {
    const resource = entry?.resource;
    if (!resource) return;
    const dateKey = getObservationEffectiveDate(resource)?.format("YYYY-MM-DD");
    if (!dateKey) return;

    const existing = byDay.get(dateKey);
    if (!existing) {
      byDay.set(dateKey, entry);
      return;
    }

    const existingTs = getObservationRecencyTimestamp(existing.resource!);
    const candidateTs = getObservationRecencyTimestamp(resource);
    if (candidateTs > existingTs) {
      byDay.set(dateKey, entry);
      return;
    }
    if (candidateTs === existingTs) {
      const existingId = Number(existing.resource?.id) || 0;
      const candidateId = Number(resource.id) || 0;
      if (candidateId > existingId) byDay.set(dateKey, entry);
    }
  });

  return [...others, ...Array.from(byDay.values())];
}

export function aggregateWearableMetricForPeriod(
  loinc: string,
  entries: FhirObservationEntry[],
  rangeDays: number,
  startDate: Dayjs,
  endDate: Dayjs,
  mode: WearableAggregationMode
): { aggregatedNumeric: number | null; dailyPoints: DailyMetricPoint[] } {
  const dedupedEntries = dedupeMetricEntriesByDay(entries, loinc);
  const dateKeys = buildDateKeysInRange(startDate, endDate);
  const valuesByDay = new Map<string, number[]>();

  dateKeys.forEach((key) => valuesByDay.set(key, []));

  dedupedEntries.forEach((entry) => {
    const resource = entry?.resource;
    if (!resource) return;
    if (getPrimaryLoincCode(resource) !== loinc) return;
    const effectiveDate = getObservationEffectiveDate(resource);
    if (!effectiveDate) return;
    const dateKey = effectiveDate.format("YYYY-MM-DD");
    if (!valuesByDay.has(dateKey)) return;
    const numeric = extractNumericValueFromObservation(resource);
    if (numeric === null) return;
    valuesByDay.get(dateKey)?.push(numeric);
  });

  const dailyPoints: DailyMetricPoint[] = dateKeys.map((dateKey) => {
    const dayValues = valuesByDay.get(dateKey) ?? [];
    const dayAggregate = aggregateValues(dayValues, mode, 1);

    return {
      dateKey,
      dateLabel: dayjs(dateKey).format("DD/MM"),
      value: dayAggregate,
      displayValue:
        dayAggregate !== null
          ? formatNumericForDisplay(loinc, dayAggregate, true)
          : "—",
    };
  });

  const dailyValues = dailyPoints
    .map((point) => point.value)
    .filter((value): value is number => value !== null);

  const aggregatedNumeric = aggregateValues(dailyValues, mode, rangeDays);

  return { aggregatedNumeric, dailyPoints };
}

export function buildWearableMetricSummaries(
  entries: FhirObservationEntry[],
  rangeDays: number,
  startDate: Dayjs,
  endDate: Dayjs,
  theme: Theme,
  mode: WearableViewMode = "fitness"
): WearableMetricSummary[] {
  const catalog = getWearableMetricCatalogForMode(mode);

  return catalog.map((metric) => {
    const presentation = getWearableMedicalCardPresentation(
      { code: { coding: [{ code: metric.loinc }] } },
      theme
    );
    const { aggregatedNumeric, dailyPoints } = aggregateWearableMetricForPeriod(
      metric.loinc,
      entries,
      rangeDays,
      startDate,
      endDate,
      metric.aggregationMode
    );
    const hasData = aggregatedNumeric !== null;

    return {
      loinc: metric.loinc,
      label: metric.label,
      unit: metric.unit,
      color: presentation.color,
      icon: presentation.icon,
      aggregatedNumeric,
      aggregatedValue: hasData
        ? formatNumericForDisplay(metric.loinc, aggregatedNumeric, false)
        : "—",
      hasData,
      dailyPoints,
    };
  });
}

export function pickDefaultWearableMetric(
  summaries: WearableMetricSummary[]
): string | null {
  const firstWithData = summaries.find((s) => s.hasData);
  return firstWithData?.loinc ?? null;
}

export function resolveSelectedMetricKey(
  summaries: WearableMetricSummary[],
  currentKey: string | null
): string | null {
  if (currentKey) {
    const current = summaries.find((s) => s.loinc === currentKey);
    if (current?.hasData) return currentKey;
  }
  return pickDefaultWearableMetric(summaries);
}
